import { Component, ViewChild, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AircraftService } from 'src/app/service/aircraft.service';
import { Aircraft } from 'src/app/models';
import { CatalogPipe, CODES, DOMAIN } from 'src/app/catalog';
import { minutesToHhMm } from 'src/app/utils/time.utils';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map, Observable } from 'rxjs';
import { ResponsiveService } from 'src/app/shared/responsive.service';
import { AppScreen } from 'src/app/shared/responsive.breakpoints';

@Component({
  selector: 'app-aircraft-list',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatProgressBarModule,
    MatTooltipModule,
    CatalogPipe,
  ],
  templateUrl: './aircraft-list.component.html',
  styleUrls: ['./aircraft-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    // Animation du chevron
    trigger('chev', [
      state('collapsed', style({ transform: 'rotate(0deg)' })),
      state('expanded', style({ transform: 'rotate(90deg)' })),
      transition('expanded <=> collapsed', animate('120ms ease')),
    ]),
    // Animation de la ligne de détail
    trigger('detailRow', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, visibility: 'hidden' }),
        animate('150ms ease-out', style({ height: '*', opacity: 1, visibility: 'visible' })),
      ]),
      transition(':leave', [animate('120ms ease-in', style({ height: '0px', opacity: 0, visibility: 'hidden' }))]),
    ]),
  ],
})
export class AircraftListComponent implements AfterViewInit {
  @ViewChild(MatTable) table!: MatTable<Aircraft>;
  readonly DOMAIN = DOMAIN;

  private readonly responsive = inject(ResponsiveService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly aircraftService = inject(AircraftService);

  dataSource = new MatTableDataSource<Aircraft>([]);

  /** Colonnes principales (triables) */
  displayedColumns: string[] = ['expand', 'code', 'condition', 'location', 'counter'];
  columnsConfig = {
    tablet: ['code', 'model', 'condition', 'location', 'counter', 'overhaulType'],
    mobileLarge: ['code', 'model', 'condition', 'location', 'counter'],
    mobile: ['code', 'condition', 'location', 'counter'],
    mobileSmall: ['code', 'condition', 'counter'],
  };

  /** Id de la ligne déployée */
  expandedId: string | null = null;

  /** Tri logique pour la condition (du “mieux” au “pire”) */
  private static readonly CONDITION_ORDER: Record<string, number> = {
    [CODES.CONDITION.AIRWORTHY]: 0,
    [CODES.CONDITION.MAINTENANCE_DUE]: 1,
    [CODES.CONDITION.FINDINGS]: 2,
    [CODES.CONDITION.GROUNDED]: 3,
  };

  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  constructor() {
    // Récupération des avions avec auto-unsubscribe
    this.aircraftService
      .getAircrafts()
      .pipe(takeUntilDestroyed())
      .subscribe((aircrafts) => {
        this.dataSource.data = aircrafts ?? [];
        this.cdr.markForCheck(); // OnPush : force la vérif visuelle
      });
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;

    // Normalise les champs pour un tri cohérent
    this.dataSource.sortingDataAccessor = (row, column) => {
      switch (column) {
        case 'code':
          return (row.code ?? '').toLowerCase();
        case 'location':
          return (row.location ?? '').toLowerCase();
        case 'condition': {
          // Tri par ordre métier + fallback sur le texte
          const weight = AircraftListComponent.CONDITION_ORDER[row.condition ?? ''] ?? 99;
          return weight;
        }
        case 'counter':
          return row.currentCounterMinutes ?? -Infinity;
        default:
          // Permet le tri sur d’autres colonnes si ajoutées plus tard
          const v = (row as any)[column];
          return typeof v === 'string' ? v.toLowerCase() : v;
      }
    };

    // Tri initial
    const initialSort: Sort = { active: 'code', direction: 'asc' };
    this.sort.active = initialSort.active;
    this.sort.direction = initialSort.direction;
    this.sort.sortChange.emit(initialSort);

    this.observeBreakpoints();
  }

  private observeBreakpoints(): void {
    this.responsive.screen$.subscribe((screen) => {
      let cols: string[];

      switch (screen) {
        case AppScreen.MobileSmall:
          cols = this.columnsConfig.mobileSmall;
          break;
        case AppScreen.Mobile:
          cols = this.columnsConfig.mobile;
          break;
        case AppScreen.MobileLarge:
        case AppScreen.TabletSmall:
          cols = this.columnsConfig.mobile;
          break;
        case AppScreen.TabletLarge:
        case AppScreen.Desktop:
        case AppScreen.DesktopLarge:
        case AppScreen.Wide:
        default:
          cols = this.columnsConfig.tablet;
          break;
      }

      // ajoute toujours la colonne 'expand' en tête
      this.displayedColumns = ['expand', ...cols];

      // OnPush + MatTable : applique + rerender
      this.cdr.detectChanges();
      this.table?.renderRows();
      (this.table as any)?.updateStickyColumnStyles?.(); // si header sticky
    });
  }

  public isMobile$: Observable<boolean> = this.breakpointObserver.observe(['(max-width: 460px)']).pipe(map((result) => result.matches));

  /** Predicate “when” pour la ligne de détail */
  isDetailRow = (_: number, row: Aircraft) => row.id === this.expandedId;

  /** Ouvre/ferme la ligne */
  toggleRow(row: Aircraft): void {
    if (!row?.id) return;
    this.expandedId = this.expandedId === row.id ? null : row.id;

    // OnPush + MatTable : force le cycle + re-render des rows
    this.cdr.detectChanges(); // assure que l’état est à jour
    this.table.renderRows(); // ✅ redessine les lignes (ajoute/enlève la ligne détail)
  }

  /** État développé ? */
  isExpanded(row: Aircraft): boolean {
    return this.expandedId === row.id;
  }

  nextMaintenance(aircraft: Aircraft): number {
    return aircraft.hoursAtMaintenance - aircraft.overhaulType;
  }

  /** Couleur de chip selon la condition */
  conditionChipColor(s: string): string {
    switch (s) {
      case CODES.CONDITION.AIRWORTHY:
        return 'chip-airworthy';
      case CODES.CONDITION.MAINTENANCE_DUE:
        return 'chip-maintenance';
      case CODES.CONDITION.GROUNDED:
        return 'chip-grounded';
      case CODES.CONDITION.FINDINGS:
        return 'chip-findings';
      default:
        return '';
    }
  }

  /** (optionnel) optimisation de rendu des lignes */
  trackById = (_: number, row: Aircraft) => row.id;

  formatHoursToMinutes(hours: number): string {
    return hours + ':00';
  }

  formatMinutesToHours(mins: number): string {
    return minutesToHhMm(mins);
  }
}
